"use client";

import { useId, useRef, useState, type ReactNode, type KeyboardEvent } from "react";

export type ProfileTab = {
  key: string;
  label: string;
  icon: ReactNode;
  count?: string;
  /** Owner-only management views are marked so the strip can set them apart. */
  owner?: boolean;
  panel: ReactNode;
};

/**
 * The profile's tab strip. ARIA tabs with the roving tabindex: one tab stop,
 * arrows move and select, Home/End jump. Only the active panel is mounted, as
 * the Radix tabs before it did — an inactive tab costs no request.
 */
export default function ArtistProfileTabs({ tabs, label }: { tabs: ProfileTab[]; label: string }) {
  const id = useId();
  const [active, setActive] = useState(tabs[0]?.key);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const current = tabs.find((tab) => tab.key === active) ?? tabs[0];

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.key === current.key);
    const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 } as Record<string, number>;
    let next = -1;
    if (event.key in step) next = (index + step[event.key] + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    setActive(tabs[next].key);
    refs.current[tabs[next].key]?.focus();
  };

  return (
    <div className="v2-ptabs-wrap">
      <div className="v2-ptabs" role="tablist" aria-label={label} onKeyDown={move}>
        {tabs.map((tab) => {
          const selected = tab.key === current.key;
          return (
            <button
              key={tab.key}
              ref={(node) => { refs.current[tab.key] = node; }}
              type="button"
              role="tab"
              id={`${id}-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`${id}-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              className="v2-ptab v2-focus"
              data-owner={tab.owner || undefined}
              onClick={() => setActive(tab.key)}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.count ? <b className="v2-ptab-count">{tab.count}</b> : null}
            </button>
          );
        })}
      </div>
      <div
        className="v2-ptab-panel"
        role="tabpanel"
        id={`${id}-panel-${current.key}`}
        aria-labelledby={`${id}-tab-${current.key}`}
        key={current.key}
      >
        {current.panel}
      </div>
    </div>
  );
}
